const buildUrl = (serverAddress, port) => {
  const hasProtocol = /^https?:\/\//i.test(serverAddress)
  const normalizedAddress = hasProtocol ? serverAddress : `http://${serverAddress}`
  const url = new URL(normalizedAddress)

  if (!url.port && port) {
    url.port = String(port)
  }

  url.pathname = '/api/public/core/v1/flash/items'
  url.search = ''
  url.hash = ''

  return url.toString()
}

// Last PRICER API call result registry (used by the advanced debugging display)
let lastCallResult = null
const callResultListeners = new Set()

const getLastCallResult = () => lastCallResult

const subscribeToCallResults = (listener) => {
  callResultListeners.add(listener)
  return () => callResultListeners.delete(listener)
}

const recordCallResult = (action, eans, result) => {
  lastCallResult = { action, eans, result, timestamp: Date.now() }
  callResultListeners.forEach((listener) => {
    try {
      listener(lastCallResult)
    } catch (error) {
      console.error('Pricer call result listener error', error)
    }
  })
}

const sendFlash = async (serverAddress, port, auth_key, eans, duration) => {
  try {
    await fetch(buildUrl(serverAddress, port), {
      method: 'POST',
      mode: 'no-cors',
      headers: {
        accept: '*/*',
        Authorization: auth_key,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        configuration: { duration, realtime: true, color: '#ff00ff', flashType: 10 },
        itemIds: eans,
      }),
    })
    // no-cors responses are opaque: status code/details cannot be read
    return { success: true, httpCode: null, errorMessage: null, durationMs: null, detailsAvailable: false }
  } catch(error) {
    return { success: false, httpCode: null, errorMessage: error?.message || 'Network error', durationMs: null, detailsAvailable: false }
  }
}

const sendFlashAndroid = async (serverAddress, port, auth_key, eans, duration) => {
  try {
    const android = typeof window !== 'undefined' ? window.Android : null

    if (!android || typeof android.pricerFlashItem !== 'function') {
      return { success: false, httpCode: null, errorMessage: 'Android bridge unavailable', durationMs: null, detailsAvailable: false }
    }

    const eansJson = JSON.stringify(Array.isArray(eans) ? eans : [])

    if (typeof android.pricerFlashItemDetailed === 'function') {
      const parsed = JSON.parse(android.pricerFlashItemDetailed(
        serverAddress,
        Number(port),
        auth_key,
        eansJson,
        Number(duration),
      ))
      return {
        success: Boolean(parsed.success),
        httpCode: parsed.httpCode ?? null,
        errorMessage: parsed.errorMessage ?? null,
        durationMs: parsed.durationMs ?? null,
        detailsAvailable: true,
      }
    }

    // Older app versions: boolean bridge only, no HTTP details
    const success = !!android.pricerFlashItem(
      serverAddress,
      Number(port),
      auth_key,
      eansJson,
      Number(duration),
    )
    return { success, httpCode: null, errorMessage: success ? null : 'Flash call failed', durationMs: null, detailsAvailable: false }
  } catch(error) {
    return { success: false, httpCode: null, errorMessage: error?.message || 'Bridge error', durationMs: null, detailsAvailable: false }
  }
}

const sendFlashSmart = async (serverAddress, port, auth_key, eans, duration, action) => {
  const android = typeof window !== 'undefined' ? window.Android : null
  const hasAndroidBridge = android && typeof android.pricerFlashItem === 'function'
  console.log('Using flash method:', hasAndroidBridge ? 'Android Bridge' : 'Fetch API')
  const result = hasAndroidBridge
    ? await sendFlashAndroid(serverAddress, port, auth_key, eans, duration)
    : await sendFlash(serverAddress, port, auth_key, eans, duration)
  recordCallResult(action, eans, result)
  return result
}

const flashItemsDetailed = (serverAddress, port, auth_key, eans) => sendFlashSmart(serverAddress, port, auth_key, eans, 60, 'flash')
const stopItemsDetailed = (serverAddress, port, auth_key, eans) => sendFlashSmart(serverAddress, port, auth_key, eans, 0, 'stop')

const flashItems = async (serverAddress, port, auth_key, eans) =>
  (await flashItemsDetailed(serverAddress, port, auth_key, eans)).success

const stopItems = async (serverAddress, port, auth_key, eans) =>
  (await stopItemsDetailed(serverAddress, port, auth_key, eans)).success

export { flashItems, stopItems, flashItemsDetailed, stopItemsDetailed, getLastCallResult, subscribeToCallResults }
